import type { QueryLabel } from '../GlobalMapSearch.classify';
import type { SearchRuntimeFlags } from './runtimeContext';

export interface SearchExecutionPlan {
  runGisLayers: boolean;
  runGeocode: boolean;
  runStaticLayers: boolean;
  runDynamicLayers: boolean;
  runCalls: boolean;
  runTranscripts: boolean;
  runMessages: boolean;
  runAlerts: boolean;
}

/**
 * Pure mapping from the classifier's primary label + runtime data flags to
 * the set of executors the orchestrator should schedule.
 *
 * Behavior matches the inline gates that used to live in `useExecuteSearch`,
 * with one product override:
 *   - Transcript, text-message, and alert/ADR searches are always included
 *     for regular search submits, regardless of classifier label.
 *
 * Remaining behavior:
 *   - Dynamic (Axon + generic dynamic spatial layers), static layers, and
 *     geocode/POI run only when the query reads as a `name` or `location`.
 *   - `place` queries (POI / address-like text) also run geocode so results
 *     include global geocoder candidates in addition to local GIS layers.
 *     `runDynamicLayers` is always true on name/location so the Axon
 *     pipeline still fires even if no other dynamic layers are loaded; the
 *     generic dynamic layer search internally short-circuits on empty
 *     `layers`.
 *   - GIS layer search runs whenever there are active layers; per-kind
 *     filters (skip hydrant on phone, restrict kinds on parcel) stay inside
 *     the GIS executor itself.
 */
export function getSearchExecutionPlan(
  primary: QueryLabel | undefined,
  flags: SearchRuntimeFlags,
): SearchExecutionPlan {
  switch (primary) {
    case 'name':
    case 'location':
      return {
        runGisLayers: flags.hasGisLayers,
        // Geocode can run globally even without a map-center bias.
        runGeocode: true,
        runStaticLayers: flags.hasActiveStaticLayers,
        runDynamicLayers: true,
        runCalls: flags.hasCalls,
        // Transcripts can hold names ("John"), locations ("Main St"), and
        // free-form context — search them on any name/location query.
        runTranscripts: flags.hasTranscriptions,
        // Same reasoning as transcripts: chat messages tied to active
        // calls often contain caller names, addresses, and context.
        runMessages: flags.hasMessages,
        runAlerts: flags.hasAlerts,
      };
    case 'phone':
      return {
        runGisLayers: flags.hasGisLayers,
        runGeocode: false,
        runStaticLayers: false,
        runDynamicLayers: false,
        runCalls: flags.hasCalls,
        // Always include text/transcript surfaces even for digit-heavy
        // terms so regular search behaves consistently across query types.
        runTranscripts: flags.hasTranscriptions,
        runMessages: flags.hasMessages,
        runAlerts: flags.hasAlerts,
      };
    case 'place':
      return {
        runGisLayers: flags.hasGisLayers,
        // Always include geocode for place/address-like queries so the
        // dropdown can expose non-local candidates under the "place" pill.
        runGeocode: true,
        runStaticLayers: false,
        runDynamicLayers: false,
        runCalls: false,
        runTranscripts: flags.hasTranscriptions,
        runMessages: flags.hasMessages,
        runAlerts: flags.hasAlerts,
      };
    case 'parcel':
    case 'badge':
    default:
      return {
        runGisLayers: flags.hasGisLayers,
        runGeocode: false,
        runStaticLayers: false,
        runDynamicLayers: false,
        runCalls: false,
        // Free-text fallback still scans transcript/message/alert surfaces
        // so classifier misses don't suppress these high-signal channels.
        runTranscripts: flags.hasTranscriptions,
        runMessages: flags.hasMessages,
        runAlerts: flags.hasAlerts,
      };
  }
}
